import { Component, OnInit, Optional, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { RolesService } from '../../../../core/services/user/roles.service';
import { lettersOnlyValidator } from '../../../shared/validators/validators';
import { CurrentUserService } from '../../../../core/services/auth/current-user.service';
import { FullscreenOverlayComponent } from '../../../components/fullscreen-overlay/fullscreen-overlay.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UserSessionModel } from '../../../../core/models/user/current-user.model';
import { ToastService } from '../../../../core/services/toast.service';
import { UserService } from '../../../../core/services/user/user.service';
import { OrganizationService } from 'app/core/services/organization/organization.service';
import { Organization } from 'app/core/models/organization/organization.model';
import { FacilityService } from 'app/core/services/organization/facility.service';
import { UpdateUserRequest } from 'app/core/models/user/user.model';
import { GenderUtils } from 'app/utils/gender-utils';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import {
  SearchCountryField,
  CountryISO,
  PhoneNumberFormat,
} from 'ngx-intl-tel-input';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { PhoneInputDirective } from '../../../../shared/directives/phone-input.directive';
import { SelectComponent } from 'app/shared/components/select/select.component';
import { getCountryCodeFromDialCode } from '../../../../utils/country-code-utils';

function noOldDateValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) {
    return null;
  }

  const inputDate = new Date(control.value);
  const minDate = new Date('1900-01-01');

  if (inputDate < minDate) {
    return { oldDate: true };
  }

  return null;
}

@Component({
  selector: 'app-edit-user',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    FullscreenOverlayComponent,
    TranslateModule,
    NgxIntlTelInputModule,
    PhoneInputDirective,
    SelectComponent,
  ],
  templateUrl: './edit-user.component.html',
  styleUrl: './edit-user.component.scss',
})
export class EditUserComponent implements OnInit {
  @Input() data?: { userId?: string };
  userForm: FormGroup;
  roles: any = [];
  userId: string = '';
  organizationList: any = [];
  organizationFacilitiesList: any = [];
  selectedFacilities: any;
  maxDate: string;
  currentUser: UserSessionModel | null = null;
  isOverlayLoading: boolean = false;
  phoneNumberPattern = '^[0-9]*$';
  organizations: Organization[] = [];
  isLoadingOrganizations = false;
  facilities: any[] = [];
  isLoadingFacilities = false;
  genders: Array<{ id: number; name: string }> = [];
  defaultCountry: CountryISO = CountryISO.Turkey;
  CountryISO = CountryISO;
  SearchCountryField = SearchCountryField;
  PhoneNumberFormat = PhoneNumberFormat;
  isFacilityAdmin: boolean = false;
  currentFacilityName: string = '';

  constructor(
    private router: Router,
    private rolesService: RolesService,
    private currentUserService: CurrentUserService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private toastService: ToastService,
    private userService: UserService,
    private organizationService: OrganizationService,
    private facilityService: FacilityService,
    @Optional() public activeModal?: NgbActiveModal,
  ) {
    const userLang = localStorage.getItem('app-lang') || 'en-US';
    this.translate.use(userLang);

    const today = new Date();
    this.maxDate = today.toISOString().split('T')[0];

    this.userForm = this.fb.group({
      roleId: [null, Validators.required],
      organizationId: [null, Validators.required],
      facilityId: [{ value: null, disabled: true }, Validators.required],
      countryCode: [''],
      gender: [null, Validators.required],
      name: [
        null,
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(100),
          Validators.pattern(/^[a-zA-ZşŞıİçÇöÖüÜĞğ\s']+$/),
        ],
      ],
      surname: [
        null,
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(100),
          Validators.pattern(/^[a-zA-ZşŞıİçÇöÖüÜĞğ\s']+$/),
        ],
      ],
      pid: [null],
      dateOfBirth: [null, [Validators.required, noOldDateValidator]],
      phoneNumber: [null, [Validators.required]],
      email: [
        null,
        [Validators.required, Validators.email, Validators.maxLength(50)],
      ],
    });
  }

  ngOnInit(): void {
    this.getUserById();
    this.currentUser = this.currentUserService.GetSession();
    this.isFacilityAdmin =
      this.currentUserService.hasClaim('facility_admin_claim');
    const userOrgId = this.currentUser?.userOrganizations?.[0]?.organizationId;

    if (!userOrgId) {
      this.getOrganizations();
    }

    const routeId = this.route.snapshot.paramMap.get('id');
    const modalId = this.data?.userId;
    this.userId = routeId || modalId || '';

    if (this.userId) {
      this.getUserById();
    } else {
      console.error('EditUserComponent: userId bulunamadı');
    }

    this.userForm.get('organizationId')?.valueChanges.subscribe((orgId) => {
      if (orgId) {
        this.loadFacilities(orgId);
        this.userForm.patchValue({ facilityId: null });
        this.getRoles(orgId);
      } else {
        this.facilities = [];
        this.userForm.patchValue({ facilityId: null, roleId: null });
        this.userForm.get('facilityId')?.disable();
      }
    });

    GenderUtils.initializeTranslateService(this.translate);
    this.genders = GenderUtils.getAllGenders();
  }

  preventForbiddenCharacters(
    event: Event,
    type: 'number' | 'text' | 'email',
  ): void {
    const inputElement = event.target as HTMLInputElement;
    let val: string | number = inputElement.value;

    if (type === 'number') {
      val = Number(inputElement.value);
      if (isNaN(val) || val < 1) {
        inputElement.value = '';
        return;
      }
    } else if (type === 'text') {
      if (/^[\s]|[^a-zA-ZğüşıöçĞÜŞİÖÇ\s]/.test(val)) {
        inputElement.value = val.replace(/^[\s]+|[^a-zA-ZğüşıöçĞÜŞİÖÇ\s]/g, '');
      }
      if (inputElement.value.trim() === '') {
        inputElement.value = '';
      }
    } else if (type === 'email') {
      if (/^[\s]|[^a-zA-Z0-9@._]/.test(val)) {
        inputElement.value = val.replace(/^[\s]+|[^a-zA-Z0-9@._]/g, '');
      }
      if (inputElement.value.trim() === '') {
        inputElement.value = '';
      }
    }
  }

  getUserById() {
    if (this.userId) {
      this.userService.getUserById(this.userId).subscribe({
        next: (res) => {
          const userData = res.data;

          let orgId = userData.organizationId;
          let facId = userData.facilityId;

          if (!orgId && userData.userOrganizations?.length > 0) {
            const firstOrg = userData.userOrganizations[0];
            orgId = firstOrg.organizationId;

            if (firstOrg.userFacilities?.length > 0) {
              facId = firstOrg.userFacilities[0].facilityId;
              this.currentFacilityName = firstOrg.userFacilities[0].facilityName;
            } else if (firstOrg.facilities?.length > 0) {
              facId = firstOrg.facilities[0].facilityId;
              this.currentFacilityName = firstOrg.facilities[0].name;
            }
          }

          this.userForm.patchValue({
            name: userData.firstName || userData.name,
            surname: userData.lastName || userData.surname,
            email: userData.email,
            phoneNumber: userData.phone || userData.phoneNumber,
            pid: userData.pid,
            roleId: userData.role.id,
            gender: userData.gender,
            dateOfBirth: userData.birthDate
              ? userData.birthDate.slice(0, 10)
              : userData.dateOfBirth
                ? userData.dateOfBirth.slice(0, 10)
                : null,
            countryCode: userData.countryCode,
          });

          if (orgId) {
            this.userForm
              .get('organizationId')
              ?.setValue(orgId, { emitEvent: false });
            this.loadFacilities(orgId);
            this.getRoles(orgId);
          }
          if (facId) {
            this.userForm.get('facilityId')?.setValue(facId);
            this.userForm.get('facilityId')?.enable();
          }
          
          // Extract country code from user phone number
          if (userData.countryCode) {
            const countryCode = getCountryCodeFromDialCode(userData.countryCode);
            if (countryCode) {
              this.defaultCountry = countryCode as CountryISO;
            }
          }
        },
        error: (err) => {
          console.error('Error loading user:', err);
          this.toastService.showToast(
            this.translate.instant('errorLoadingUser'),
            'error',
          );
        },
      });
    }
  }

  getRoles(organizationId: string | null = null) {
    this.rolesService.getRoles(0, 1000, organizationId).subscribe({
      next: (roles) => {
        this.roles = (roles?.data?.items ?? []).map((x: any) => ({
          id: x.role?.id,
          name: x.role?.name,
        }));
      },
      error: (err) => {
        console.error('getRoles error:', err);
        this.toastService.showToast(
          'Role listesi yüklenirken hata oluştu.',
          'error',
        );
      },
    });
  }

  getOrganizations() {
    this.isLoadingOrganizations = true;

    this.organizationService.getAllOrganizationItems().subscribe({
      next: (items) => {
        this.organizations = items;
        this.isLoadingOrganizations = false;
      },
      error: () => {
        this.isLoadingOrganizations = false;
      },
    });
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toISOString();
  }

  saveUser(e: Event) {
    e.preventDefault();

    if (!this.userId) {
      this.toastService.showToast(
        this.translate.instant('errorEditUserDesc'),
        'error',
      );
      return;
    }

    if (this.userForm.invalid) {
      this.toastService.showToast(
        this.translate.instant('warningAllFieldsAreRequiredDesc'),
        'warning',
      );
      this.userForm.markAllAsTouched();
      return;
    }

    this.isOverlayLoading = true;

    this.isOverlayLoading = true;
    let selectedOrgId = '';
    let selectedFacilityId = '';
    let selectedFacilityName = '';
    let selectedOrgName = '';

    if (this.isFacilityAdmin) {
      if (!this.currentUser?.userOrganizations?.length) {
        this.toastService.showToast(
          'User organization not found for facility admin',
          'error'
        );
        this.isOverlayLoading = false;
        return;
      }
      const userOrg = this.currentUser.userOrganizations[0];
      selectedOrgId = userOrg.organizationId;
      selectedOrgName = userOrg.organizationName;

      const loadedFacId = this.userForm.get('facilityId')?.value;
      if (loadedFacId) {
        selectedFacilityId = loadedFacId;
        selectedFacilityName = this.currentFacilityName;
      }
    } else {
      selectedOrgId = this.userForm.value.organizationId;
      const selectedOrg = this.organizations.find(
        (o) => o.id === selectedOrgId
      );
      selectedOrgName = selectedOrg?.name || '';
      selectedFacilityId = this.userForm.value.facilityId;
      const selectedFacility = this.facilities.find(
        (f) => f.id === selectedFacilityId
      );
      selectedFacilityName = selectedFacility?.name || '';
    }

    const phoneValue = this.userForm.value.phoneNumber;
    const phoneNumber = phoneValue?.number
      ? phoneValue.number.replace(/\D/g, '')
      : '';
    const countryCode = phoneValue?.dialCode ?? '';

    const formData: UpdateUserRequest = {
      id: this.userId,
      firstName: this.userForm.value.name,
      lastName: this.userForm.value.surname,
      email: this.userForm.value.email,
      phone: phoneNumber,
      countryCode: countryCode,
      birthDate: this.formatDate(this.userForm.value.dateOfBirth),
      pid: this.userForm.value.pid,
      roleId: this.userForm.value.roleId,
      gender: Number(this.userForm.value.gender),
      isProfile: false,
      avatar: null,
      userOrganizationFacility: {
        organizationId: selectedOrgId,
        organizationName: selectedOrgName,
        facilities: selectedFacilityId
          ? [
              {
                facilityId: selectedFacilityId,
                facilityName: selectedFacilityName,
              },
            ]
          : [],
      },
    };

    this.userService.updateUser(formData).subscribe({
      next: () => {
        this.toastService.showToast(
          this.translate.instant('successEditUserDesc'),
          'success',
        );

        if (this.activeModal) {
          this.activeModal.close('success');
        } else {
          setTimeout(() => {
            this.router.navigate(['/user/list']);
          }, 1000);
        }
      },
      error: () => {
        this.isOverlayLoading = false;
      },
      complete: () => {
        this.isOverlayLoading = false;
      },
    });
  }

  onCancel() {
    if (this.activeModal) {
      this.activeModal.dismiss('cancel');
    } else {
      this.router.navigate(['/user/list']);
    }
  }

  isControlInvalid(controlName: string): boolean {
    const control = this.userForm.get(controlName);
    return control
      ? control.invalid && (control.touched || control.dirty)
      : false;
  }

  onChangeSelectedFacilities(event: any) {
    const selectedFacilitiesId = (event.target as HTMLSelectElement).value;
    this.selectedFacilities = this.organizationFacilitiesList.find(
      (item: any) => item.id == selectedFacilitiesId,
    );
  }

  allowOnlyNumbers(event: KeyboardEvent): void {
    const charCode = event.key.charCodeAt(0);
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }

  loadFacilities(organizationId: string) {
    if (this.isFacilityAdmin) return;
    if (!organizationId) {
      this.facilities = [];
      this.userForm.patchValue({ facilityId: null });
      this.userForm.get('facilityId')?.disable();
      return;
    }

    this.isLoadingFacilities = true;

    this.userForm.get('facilityId')?.disable();

    this.facilityService
      .getAllFacilitiesByOrganization(organizationId)
      .subscribe({
        next: (res: any) => {
          this.facilities = res?.data ?? [];
          this.isLoadingFacilities = false;

          if (this.facilities.length > 0) {
            this.userForm.get('facilityId')?.enable();
          } else {
            this.userForm.get('facilityId')?.disable();
          }
        },
        error: () => {
          this.isLoadingFacilities = false;
          this.userForm.get('facilityId')?.disable();
        },
      });
  }
}
